import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { CallbackEvent } from "@shopify/polaris-types";

export type ActiveTab =
  | "upsells"
  | "quantity-offers"
  | "bundles"
  | "abandoned-cart";

type SIconType = React.JSX.IntrinsicElements["s-icon"]["type"];

const TABS: { id: ActiveTab; label: string; icon: SIconType; to: string }[] = [
  {
    id: "upsells",
    label: "Upsells y Downsells",
    icon: "cart-up",
    to: "/sales-booster",
  },
  {
    id: "quantity-offers",
    label: "Ofertas de Cantidad",
    icon: "price-list",
    to: "/sales-booster/quantity-offers",
  },
  {
    id: "bundles",
    label: "Bundles",
    icon: "product",
    to: "/sales-booster/bundles",
  },
  {
    id: "abandoned-cart",
    label: "Carrito Abandonado",
    icon: "cart-abandoned",
    to: "/sales-booster?tab=abandoned-cart",
  },
];

function useActiveTab(): ActiveTab {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  if (
    location.pathname.startsWith("/sales-booster/upsells-1-click") ||
    location.pathname.startsWith("/sales-booster/upsells-1-tick")
  ) {
    return "upsells";
  }
  if (location.pathname.startsWith("/sales-booster/downsells")) {
    return "upsells";
  }
  if (location.pathname.startsWith("/sales-booster/quantity-offers")) {
    return "quantity-offers";
  }
  if (location.pathname.startsWith("/sales-booster/bundles")) {
    return "bundles";
  }
  return (searchParams.get("tab") as ActiveTab) ?? "upsells";
}

export function SalesBoosterTabBar() {
  const active = useActiveTab();
  const navigate = useNavigate();

  const handleClick = (to: string, e: CallbackEvent<"s-clickable">) => {
    const mouseEvent = e as unknown as MouseEvent;
    // Let the browser handle modifier-click (open in new tab/window)
    if (
      mouseEvent.metaKey ||
      mouseEvent.ctrlKey ||
      mouseEvent.shiftKey ||
      mouseEvent.altKey
    ) {
      return;
    }
    e.preventDefault();
    navigate(to);
  };

  return (
    <nav className="flex items-center bg-white p-1 rounded-full shadow-sm border border-gray-200/50">
      {TABS.map((tab) => {
        const isActive = active === tab.id;
        return (
          <div key={tab.id} className="flex-1">
            <s-clickable
              href={tab.to}
              onClick={(e: CallbackEvent<"s-clickable">) =>
                handleClick(tab.to, e)
              }
              aria-current={isActive ? "page" : undefined}
              inlineSize="100%"
              borderRadius="large-200"
            >
              <span
                className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all ${
                  isActive
                    ? "bg-gray-100 text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <s-icon type={tab.icon} />
                {tab.label}
              </span>
            </s-clickable>
          </div>
        );
      })}
    </nav>
  );
}
